import { FormHandles } from '@unform/core';
import React, { useCallback, useMemo } from 'react';

import { InputCheckboxBox } from '../InputCheckboxBox';
import { Icon } from '../Icon';
import { colors } from '../../styles/tokens';
import {
  EMPLOYEE_PRESET,
  GYM_MANAGER_PRESET,
  groupCatalogByCategory,
  IPermission,
} from '../../utils/permissionPresets';

import {
  CategoryCount,
  CategoryHeader,
  CategoryLabel,
  CategorySection,
  Label,
  PermissionDescription,
  PermissionRow,
  PermissionsList,
  PresetButton,
  PresetsRow,
} from './styles';

export type { IPermission };

interface IPermissionsEditorProps {
  catalog: IPermission[];
  formRef: React.RefObject<FormHandles>;
  // Opcional -- item de escopo `platform` (hoje só `manage_platform_gyms`)
  // pra oferecer também um preset de "área administrativa" (plataforma).
  // Só faz sentido passar isso em telas de admin de plataforma
  // (associateGymUser/AdminUserDetail) -- nunca em ChangeDataUser, que é
  // gestão dentro da própria academia e não devia poder conceder acesso
  // de plataforma.
  platformPermission?: IPermission;
  // Estado salvo do vínculo -- só pro contador "2/4" de cada categoria
  // (não é atualizado ao vivo enquanto o admin mexe nos switches antes de
  // salvar, mesmo comportamento do GroupedPermissionsEditor). Opcional pra
  // não quebrar quem já usa o componente sem passar nada (ChangeDataUser).
  currentPermissions?: string[];
}

// Presets + lista de checkboxes de permissão de escopo `gym` -- puramente
// visual, os campos (`InputCheckboxBox` nomeados pelo `code`) se
// registram no `<Form>` mais próximo via unform, então precisa ser
// renderizado dentro de um `<Form initialData={...}>` que já tenha o
// valor inicial de cada `code` -- ver `changeDataUser`/`associateGymUser`
// pro padrão de montar `initialData` a partir do catálogo.
const PermissionsEditor: React.FC<IPermissionsEditorProps> = ({
  catalog,
  formRef,
  platformPermission,
  currentPermissions,
}) => {
  const groups = useMemo(() => groupCatalogByCategory(catalog), [catalog]);

  const currentSet = useMemo(
    () => new Set(currentPermissions ?? []),
    [currentPermissions],
  );

  const applyPreset = useCallback(
    (codes: string[]) => {
      catalog.forEach(permission => {
        formRef.current?.setFieldValue(
          permission.code,
          codes.includes(permission.code),
        );
      });

      if (platformPermission) {
        formRef.current?.setFieldValue(
          platformPermission.code,
          codes.includes(platformPermission.code),
        );
      }
    },
    [catalog, formRef, platformPermission],
  );

  return (
    <>
      <Label>Permissões:</Label>

      <PresetsRow>
        <PresetButton
          type="button"
          onClick={() => applyPreset(EMPLOYEE_PRESET)}
        >
          Funcionário padrão
        </PresetButton>
        <PresetButton
          type="button"
          onClick={() => applyPreset(GYM_MANAGER_PRESET)}
        >
          Gestor da academia
        </PresetButton>
        {platformPermission && (
          <PresetButton
            type="button"
            onClick={() =>
              applyPreset([...GYM_MANAGER_PRESET, platformPermission.code])
            }
          >
            Admin de plataforma
          </PresetButton>
        )}
        <PresetButton type="button" onClick={() => applyPreset([])}>
          Nenhuma (aluno)
        </PresetButton>
      </PresetsRow>

      <PermissionsList>
        {groups.map(group => (
          <CategorySection key={group.code}>
            <CategoryHeader>
              <Icon name={group.icon} size={16} color={colors.primary} />
              <CategoryLabel>{group.label}</CategoryLabel>
              <CategoryCount>
                {group.permissions.filter(p => currentSet.has(p.code)).length}/
                {group.permissions.length}
              </CategoryCount>
            </CategoryHeader>

            {group.permissions.map(permission => (
              <PermissionRow key={permission.id}>
                <InputCheckboxBox name={permission.code} />
                <PermissionDescription>
                  {permission.description}
                </PermissionDescription>
              </PermissionRow>
            ))}
          </CategorySection>
        ))}

        {platformPermission && (
          <CategorySection>
            <CategoryHeader>
              <Icon name="shield_person" size={16} color={colors.primary} />
              <CategoryLabel>Plataforma</CategoryLabel>
              <CategoryCount>
                {currentSet.has(platformPermission.code) ? 1 : 0}/1
              </CategoryCount>
            </CategoryHeader>

            <PermissionRow>
              <InputCheckboxBox name={platformPermission.code} />
              <PermissionDescription>
                {platformPermission.description} (plataforma)
              </PermissionDescription>
            </PermissionRow>
          </CategorySection>
        )}
      </PermissionsList>
    </>
  );
};

export { PermissionsEditor };
